// src/services/hearenProfileEngine.js

export function buildHearenProfileConfig({
  user = null,
  adaptive = null,
  tinnitus = null,
  listeningStats = null,
  audioProfileCompleted = false,
} = {}) {
  const tinnitusFrequency =
    tinnitus?.frequency ||
    user?.tinnitusFrequency ||
    adaptive?.tinnitusFrequency ||
    6000;

  const tinnitusCompleted = Boolean(
    tinnitus?.completed ||
    user?.tinnitusTestCompleted ||
    localStorage.getItem('hearen_tinnitus_test_completed') === 'true'
  );

  const resolvedAudioProfileCompleted = Boolean(
    audioProfileCompleted ||
    user?.audioProfileCompleted ||
    user?.hearingTestCompleted ||
    user?.hasAudioProfile ||
    adaptive?.completed ||
    adaptive?._fullState?.completed ||
    localStorage.getItem('hearen_audio_profile_completed') === 'true' ||
    localStorage.getItem('hearen_hearing_test_completed') === 'true'
  );

  const totalListeningMinutes =
    listeningStats?.totalMinutes ||
    user?.totalListeningMinutes ||
    0;

  const step = computeUserStep({
    audioProfileCompleted: resolvedAudioProfileCompleted,
    tinnitusCompleted,
    totalListeningMinutes,
  });

  return {
    version: 'satori_v1',

  progression: {
    step,
    stepInfo: getUserStepInfo(step),
    audioProfileCompleted: resolvedAudioProfileCompleted,
    tinnitusCompleted,
    totalListeningMinutes,
  },

    tinnitus: {
      frequency: tinnitusFrequency,
      completed: tinnitusCompleted,
      confidence: tinnitusCompleted ? 'medium' : 'none',
    },

    correction: {
      enabled: tinnitusCompleted,
      mode: 'half_octave',
      type: 'parametric',
      frequency: tinnitusFrequency,
      gainDb: 3,
      bandwidth: 0.5,
    },

    matrix: {
      version: 'classic_v1',
      higonoEnabled: false,
    },
  };
}

export function computeUserStep({
  audioProfileCompleted = false,
  tinnitusCompleted = false,
  totalListeningMinutes = 0,
} = {}) {
  if (audioProfileCompleted && tinnitusCompleted && totalListeningMinutes >= 1200) {
    return 5;
  }

  if (audioProfileCompleted && tinnitusCompleted && totalListeningMinutes >= 120) {
    return 4;
  }

  if (audioProfileCompleted && tinnitusCompleted) {
    return 3;
  }

  if (audioProfileCompleted || tinnitusCompleted) {
    return 2;
  }

  return 1;
}

export function getUserStepInfo(step) {
  switch (step) {
    case 5:
      return {
        key: 'advanced_habituation',
        labelFr: 'Habituation avancée',
        labelEn: 'Advanced habituation',
        descriptionFr: 'Votre écoute est installée dans la durée.',
        descriptionEn: 'Your listening habit is now well established.',
      };

    case 4:
      return {
        key: 'regular_listener',
        labelFr: 'Utilisateur régulier',
        labelEn: 'Regular listener',
        descriptionFr: 'Votre profil est complet et votre écoute devient régulière.',
        descriptionEn: 'Your profile is complete and your listening is becoming regular.',
      };

    case 3:
      return {
        key: 'complete_profile',
        labelFr: 'Profil complet',
        labelEn: 'Complete profile',
        descriptionFr: 'Votre profil audio et acouphène est prêt.',
        descriptionEn: 'Your audio and tinnitus profile is ready.',
      };

    case 2:
      return {
        key: 'partial_profile',
        labelFr: 'Profil partiel',
        labelEn: 'Partial profile',
        descriptionFr: 'Une première personnalisation est commencée.',
        descriptionEn: 'A first personalization step has begun.',
      };

    default:
      return {
        key: 'discovery',
        labelFr: 'Découverte',
        labelEn: 'Discovery',
        descriptionFr: 'Vous découvrez progressivement Hearen.',
        descriptionEn: 'You are gradually discovering Hearen.',
      };
  }
}

export function buildEQParamsFromHearenProfile(profileConfig) {
  const correction = profileConfig?.correction;

  if (!correction?.enabled) {
    return {
      enabled: false,
      frequency: correction?.frequency || 6000,
      gain: 0,
      bandwidth: 0.5,
      type: 'parametric',
    };
  }

  return {
    enabled: true,
    frequency: correction.frequency,
    gain: correction.gainDb,
    bandwidth: correction.bandwidth,
    type: correction.type,
  };
}